import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getSessionData } from "utils/common";
import { showMessage } from "./common.slice";
import { logOut, loginAction, instantDemo } from "./auth.slice";

const initialState = {
  marginList: [],
  marginLoading: false,
  marginUpdated: false,
  creditUpdated: false,
  riskSettings: null,
  marginSummary: null,
  creditBalance: null,
  creditEntries: [],
  creditLoading: false,
  marginEvents: [],
};

const sessionUserId = () => {
  const s = getSessionData() || {};
  return Number(s.userId ?? s.UserId ?? s.id ?? s.Id ?? 0) || 0;
};

const normalizeCreditBalance = (payload) => {
  if (!payload || typeof payload !== "object") {
    return { credit: 0, grantableCredit: 0, userId: 0, canGrantUnlimited: false };
  }
  return {
    userId: payload.userId ?? payload.UserId ?? 0,
    credit: Number(payload.credit ?? payload.Credit ?? 0),
    grantableCredit: Number(payload.grantableCredit ?? payload.GrantableCredit ?? 0),
    canGrantUnlimited: !!(payload.canGrantUnlimited ?? payload.CanGrantUnlimited),
  };
};

const apiOk = (data) => data && (data.status === 200 || data.status === "200");

const handleApi = (dispatch, promise, { successMsg } = {}) =>
  new Promise((resolve, reject) => {
    promise
      .then(({ data }) => {
        if (apiOk(data)) {
          if (successMsg || data.message) {
            dispatch(showMessage({ message: data.message || successMsg, varient: "success" }));
          }
          resolve(data.data ?? true);
        } else {
          dispatch(showMessage({ message: data.message || "Request failed" }));
          reject(data);
        }
      })
      .catch((error) => {
        dispatch(
          showMessage({
            message: error?.response?.data?.message || error.message || "Request failed",
          })
        );
        reject(error);
      });
  });

export const getMarginByUser = createAsyncThunk("margin/get-by-user", (userId) => {
  return new Promise((resolve, reject) => {
    axios
      .get(`margin/settings/get-by-user/${userId}`)
      .then(({ data }) => {
        if (apiOk(data)) resolve(data.data || []);
        else reject(data);
      })
      .catch(reject);
  });
});

export const saveMarginSetting = createAsyncThunk("margin/save", (payload, { dispatch }) => {
  const createdBy = sessionUserId();
  return handleApi(
    dispatch,
    axios.post("margin/settings/save", { ...payload, createdBy })
  );
});

export const deleteMarginSetting = createAsyncThunk("margin/delete", (id, { dispatch }) => {
  const createdBy = sessionUserId();
  return handleApi(
    dispatch,
    axios.post(`margin/settings/delete/${id}?deletedBy=${createdBy}`)
  );
});

export const getRiskSettings = createAsyncThunk("margin/risk-get", () => {
  return new Promise((resolve, reject) => {
    axios
      .get("margin/risk-settings/get")
      .then(({ data }) => {
        if (apiOk(data)) resolve(data.data || null);
        else reject(data);
      })
      .catch(reject);
  });
});

export const saveRiskSettings = createAsyncThunk("margin/risk-save", (payload, { dispatch }) => {
  const createdBy = sessionUserId();
  return handleApi(
    dispatch,
    axios.post("margin/risk-settings/save", { ...payload, createdBy })
  );
});

export const getMarginSummary = createAsyncThunk("margin/summary", (forUserId) => {
  const sessionId = sessionUserId();
  const targetId = forUserId || sessionId;
  const url = targetId ? `margin/summary/${targetId}` : "margin/summary";
  return new Promise((resolve, reject) => {
    axios
      .get(url)
      .then(({ data }) => {
        if (apiOk(data)) resolve(data.data || null);
        else reject(data);
      })
      .catch(reject);
  });
});

export const getCreditBalance = createAsyncThunk(
  "margin/credit-balance",
  (targetUserId, { dispatch }) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`margin/credit/balance/${targetUserId}`)
        .then(({ data }) => {
          if (apiOk(data)) resolve(normalizeCreditBalance(data.data));
          else {
            dispatch(showMessage({ message: data.message || "Could not load credit balance" }));
            reject(data);
          }
        })
        .catch((error) => {
          dispatch(
            showMessage({
              message:
                error?.response?.data?.message || error.message || "Could not load credit balance",
            })
          );
          reject(error);
        });
    });
  }
);

export const getCreditEntries = createAsyncThunk(
  "margin/credit-entries",
  ({ userId, pageNo = 1, pageSize = 50 }, { dispatch }) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`margin/credit/entries/${userId}/${pageNo}/${pageSize}`)
        .then(({ data }) => {
          if (apiOk(data)) resolve(data.data || []);
          else {
            dispatch(showMessage({ message: data.message || "Could not load credit entries" }));
            reject(data);
          }
        })
        .catch((error) => {
          dispatch(
            showMessage({
              message:
                error?.response?.data?.message || error.message || "Could not load credit entries",
            })
          );
          reject(error);
        });
    });
  }
);

export const creditIn = createAsyncThunk("margin/credit-in", (payload, { dispatch }) => {
  const createdBy = sessionUserId();
  return handleApi(
    dispatch,
    axios.post("margin/credit/credit-in", { ...payload, createdBy }),
    { successMsg: "Credit added successfully" }
  );
});

export const creditOut = createAsyncThunk("margin/credit-out", (payload, { dispatch }) => {
  const createdBy = sessionUserId();
  return handleApi(
    dispatch,
    axios.post("margin/credit/credit-out", { ...payload, createdBy }),
    { successMsg: "Credit removed successfully" }
  );
});

export const getMarginEvents = createAsyncThunk(
  "margin/events",
  ({ pageNo = 1, pageSize = 50, userId }) => {
    const qs = userId ? `?userId=${userId}` : "";
    return new Promise((resolve, reject) => {
      axios
        .get(`margin/events/${pageNo}/${pageSize}${qs}`)
        .then(({ data }) => {
          if (apiOk(data)) resolve(data.data || []);
          else reject(data);
        })
        .catch(reject);
    });
  }
);

const margin = createSlice({
  name: "margin",
  initialState,
  reducers: {
    setMarginUpdated: (state, action) => {
      state.marginUpdated = action.payload ?? false;
    },
    setCreditUpdated: (state, action) => {
      state.creditUpdated = action.payload ?? false;
    },
    clearCreditData: (state) => {
      state.creditBalance = null;
      state.creditEntries = [];
      state.marginSummary = null;
    },
    clearMarginSession: (state) => {
      state.marginSummary = null;
      state.creditBalance = null;
      state.creditEntries = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMarginByUser.pending, (state) => {
        state.marginLoading = true;
      })
      .addCase(getMarginByUser.fulfilled, (state, action) => {
        state.marginList = action.payload || [];
        state.marginLoading = false;
      })
      .addCase(getMarginByUser.rejected, (state) => {
        state.marginLoading = false;
      })
      .addCase(saveMarginSetting.fulfilled, (state) => {
        state.marginUpdated = true;
      })
      .addCase(deleteMarginSetting.fulfilled, (state) => {
        state.marginUpdated = true;
      })
      .addCase(getRiskSettings.fulfilled, (state, action) => {
        state.riskSettings = action.payload;
      })
      .addCase(saveRiskSettings.fulfilled, (state) => {
        state.marginUpdated = true;
      })
      .addCase(getMarginSummary.fulfilled, (state, action) => {
        const summary = action.payload;
        if (!summary) return;
        const summaryUserId = Number(summary.userId ?? summary.UserId ?? 0);
        const requestedId = action.meta?.arg;
        const sessionId = sessionUserId();
        const isSelfFetch = !requestedId || Number(requestedId) === sessionId;
        if (isSelfFetch && sessionId > 0 && summaryUserId > 0 && summaryUserId !== sessionId) {
          return;
        }
        state.marginSummary = summary;
      })
      .addCase(getMarginSummary.rejected, () => {
        // Keep last good marginSummary on transient API/refresh failures.
      })
      .addCase(logOut.fulfilled, (state) => {
        state.marginSummary = null;
        state.creditBalance = null;
        state.creditEntries = [];
      })
      .addCase(logOut.rejected, (state) => {
        state.marginSummary = null;
        state.creditBalance = null;
        state.creditEntries = [];
      })
      .addCase(loginAction.fulfilled, (state) => {
        state.marginSummary = null;
        state.creditBalance = null;
        state.creditEntries = [];
      })
      .addCase(instantDemo.fulfilled, (state) => {
        state.marginSummary = null;
        state.creditBalance = null;
        state.creditEntries = [];
      })
      .addCase(getCreditBalance.pending, (state) => {
        state.creditLoading = true;
      })
      .addCase(getCreditBalance.fulfilled, (state, action) => {
        state.creditBalance = action.payload;
        state.creditLoading = false;
      })
      .addCase(getCreditBalance.rejected, (state) => {
        state.creditLoading = false;
      })
      .addCase(getCreditEntries.pending, (state) => {
        state.creditLoading = true;
      })
      .addCase(getCreditEntries.fulfilled, (state, action) => {
        state.creditEntries = action.payload || [];
        state.creditLoading = false;
      })
      .addCase(getCreditEntries.rejected, (state) => {
        state.creditLoading = false;
      })
      .addCase(creditIn.fulfilled, (state) => {
        state.creditUpdated = true;
      })
      .addCase(creditOut.fulfilled, (state) => {
        state.creditUpdated = true;
      })
      .addCase(getMarginEvents.fulfilled, (state, action) => {
        state.marginEvents = action.payload || [];
      });
  },
});

export const { setMarginUpdated, setCreditUpdated, clearCreditData, clearMarginSession } =
  margin.actions;
export default margin.reducer;
